import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  ShieldCheck, LogOut, Users, GraduationCap, BookOpen, ClipboardList, Loader2,
  UserPlus, KeyRound, CalendarRange, Save, CheckCircle2, XCircle, School, Trash2, AlertTriangle, FileArchive,
  Download, Upload, Database,
  Sparkles, Plus, RefreshCw, Cpu, ExternalLink,
  Bell, Search, Star, Clock, Zap, FileText, Pencil, Award, History, Building2,
  Key, Copy, Link2, Globe, ArrowRightLeft,
} from "lucide-react";

const BADGE_ICON_MAP = { Clock, Zap, FileText, Award };
const KELAS_OPTS = [1, 2, 3, 4, 5, 6];

const AI_STATUS = {
  aktif: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  kuota_habis: { label: "Kuota Habis", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  nonaktif: { label: "Nonaktif", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

function AiKeysSection() {
  const [data, setData] = useState({ keys: [], total_calls: 0, max_keys: 10, uses_own_keys: false });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [delFor, setDelFor] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/gemini-keys").then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const add = async () => {
    if (!newKey.trim()) { toast.error("Masukkan API key Gemini"); return; }
    setSaving(true);
    try {
      await api.post("/admin/gemini-keys", { key: newKey.trim(), label: label.trim() });
      toast.success("API key berhasil ditambahkan & tervalidasi");
      setOpen(false); setNewKey(""); setLabel(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menambah API key"); }
    finally { setSaving(false); }
  };
  const reactivate = async (id) => {
    try { await api.put(`/admin/gemini-keys/${id}/reactivate`); toast.success("Key diaktifkan kembali"); load(); }
    catch (e) { toast.error("Gagal mengaktifkan key"); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/gemini-keys/${delFor.id}`); toast.success("API key dihapus"); setDelFor(null); load(); }
    catch (e) { toast.error("Gagal menghapus API key"); }
  };

  const full = data.keys.length >= data.max_keys;

  return (
    <section className="bg-white rounded-2xl p-6 border border-indigo-100 shadow-sm" data-testid="ai-keys-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800">API Key AI (Gemini)</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Simpan hingga <b>{data.max_keys}</b> API key Gemini milik sekolah. Jika kuota satu key habis, sistem otomatis beralih ke key berikutnya yang masih aktif.
              {!data.uses_own_keys && <span className="block mt-1 text-indigo-600">Saat ini AI memakai kunci bawaan Emergent. Tambahkan API key untuk memakai kuota sendiri.</span>}
            </p>
            <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-2" data-testid="get-gemini-key-link">
              <ExternalLink className="w-3.5 h-3.5" /> Dapatkan API key di Google AI Studio
            </a>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} disabled={full} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="add-ai-key-btn">
          <Plus className="w-4 h-4 mr-2" /> Tambah API Key
        </Button>
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-1.5 text-slate-600" data-testid="ai-total-calls"><Cpu className="w-4 h-4 text-indigo-500" /> Total pemakaian AI: <b className="text-slate-800">{data.total_calls}</b> kali</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600">Key tersimpan: <b className="text-slate-800">{data.keys.length}/{data.max_keys}</b></span>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : data.keys.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400 bg-slate-50 rounded-xl p-4 text-center" data-testid="ai-keys-empty">Belum ada API key. AI memakai kunci bawaan.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {data.keys.map((k) => {
            const st = AI_STATUS[k.status] || AI_STATUS.aktif;
            return (
              <div key={k.id} className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`ai-key-row-${k.id}`}>
                <div className="flex-1 min-w-[160px]">
                  <p className="font-semibold text-slate-800 text-sm">{k.label}</p>
                  <p className="font-mono text-xs text-slate-500 mt-0.5">{k.masked}</p>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                <div className="text-xs text-slate-500 min-w-[150px]">
                  <span className="text-emerald-600 font-semibold">{k.successes}</span> sukses · <span className="text-rose-600 font-semibold">{k.failures}</span> gagal
                </div>
                {k.status !== "aktif" && (
                  <Button size="sm" variant="outline" onClick={() => reactivate(k.id)} className="rounded-lg h-8" data-testid={`reactivate-ai-key-${k.id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Aktifkan
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setDelFor(k)} className="rounded-lg h-8 text-rose-600 hover:text-rose-700" data-testid={`delete-ai-key-${k.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="add-ai-key-dialog">
          <DialogHeader><DialogTitle className="font-display">Tambah API Key Gemini</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs text-slate-500">API Key</Label>
              <Input type="password" value={newKey} onChange={(e) => setNewKey(e.target.value)}
                     placeholder="AIza..." className="mt-1 rounded-xl font-mono" data-testid="ai-key-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Label (opsional)</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)}
                     placeholder="Contoh: Key Sekolah #1" className="mt-1 rounded-xl" data-testid="ai-key-label-input" />
            </div>
            <p className="text-xs text-slate-400">Key akan diuji ke Google AI Studio sebelum disimpan. Hanya super admin yang bisa melihat/menghapus.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={add} disabled={saving || !newKey.trim()} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="save-ai-key-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Validasi & Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus API key "${delFor?.label}"?`}
        description="Key akan dihapus permanen dari sistem. AI tidak akan memakai key ini lagi." onConfirm={remove} testId="delete-ai-key-dialog" />
    </section>
  );
}

function SourceDownload() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/source-zip", { responseType: "blob", timeout: 180000 });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : "kelas-dunia-source.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Source code (ZIP) berhasil diunduh");
    } catch (e) {
      toast.error("Gagal membuat ZIP source code");
    } finally { setBusy(false); }
  };
  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="source-download-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0"><FileArchive className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800">Unduh Source Code</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Mengunduh seluruh kode proyek (backend FastAPI + frontend React) dalam satu file ZIP, termasuk README panduan instalasi
              dan contoh <code className="text-xs bg-slate-100 px-1 rounded">.env.example</code>. File <code className="text-xs bg-slate-100 px-1 rounded">.env</code> asli, <code className="text-xs bg-slate-100 px-1 rounded">node_modules</code>, dan data database tidak disertakan.
            </p>
          </div>
        </div>
        <Button onClick={download} disabled={busy} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="download-source-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />} {busy ? "Menyiapkan ZIP..." : "Unduh ZIP"}
        </Button>
      </div>
    </section>
  );
}

function BackupRestoreSection({ onDone }) {
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState("");

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/backup/export", { responseType: "blob", timeout: 300000 });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : "kelas-dunia-backup.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup berhasil diunduh");
    } catch (e) {
      toast.error("Gagal membuat backup");
    } finally { setExporting(false); }
  };

  const doImport = async () => {
    if (!file) { toast.error("Pilih file backup .zip"); return; }
    if (mode === "replace" && confirm.trim().toUpperCase() !== "GANTI") {
      toast.error('Ketik "GANTI" untuk konfirmasi mode replace'); return;
    }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post("/admin/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 600000,
      });
      setResult(data);
      toast.success("Backup berhasil dipulihkan", { duration: 6000 });
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memulihkan backup");
    } finally { setImporting(false); }
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="backup-restore-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Database className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800">Backup &amp; Restore Data</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Ekspor seluruh data sekolah (guru, murid, mapel, tugas, pengumpulan, absensi, notifikasi) ke satu file ZIP, atau impor
              kembali dari file ZIP tersebut. Foto tugas disertakan — foto absensi dan foto profil murid tidak ikut untuk menjaga ukuran file.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={doExport} disabled={exporting} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="backup-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? "Menyiapkan..." : "Ekspor Backup"}
          </Button>
          <Button onClick={() => { setImportOpen(true); setFile(null); setResult(null); setMode("merge"); setConfirm(""); }}
                  variant="outline" className="rounded-xl" data-testid="backup-import-btn">
            <Upload className="w-4 h-4 mr-2" /> Impor Backup
          </Button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setFile(null); setResult(null); setConfirm(""); } }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="backup-import-dialog">
          <DialogHeader><DialogTitle className="font-display">Impor Backup Data</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <Label className="text-xs text-slate-500">File backup (.zip)</Label>
              <Input type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="mt-1 rounded-xl" data-testid="backup-file-input" />
              {file && <p className="text-xs text-slate-500 mt-1">{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</p>}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="backup-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Gabung (merge) — upsert, data existing dipertahankan</SelectItem>
                  <SelectItem value="replace">Ganti (replace) — hapus data lama lalu impor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "replace" && (
              <div className="bg-rose-50 rounded-xl p-3 space-y-2">
                <p className="text-rose-800 text-xs">Mode REPLACE akan menghapus semua data existing sebelum mengimpor. Tidak bisa dibatalkan.</p>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Ketik "GANTI" untuk konfirmasi'
                       className="rounded-xl" data-testid="backup-replace-confirm" />
              </div>
            )}
            {result && (
              <div className="bg-emerald-50 rounded-xl p-3 text-emerald-800 text-xs space-y-1" data-testid="backup-result">
                <p className="font-semibold">Hasil impor (mode: {result.mode})</p>
                {Object.entries(result.collections).map(([k, v]) => (
                  <p key={k}>• <b>{k}</b>: {v.inserted} baru, {v.updated} diperbarui{v.deleted ? `, ${v.deleted} dihapus` : ""}</p>
                ))}
                <p>• <b>foto</b>: {result.photos_restored} dipulihkan{result.photos_failed ? `, ${result.photos_failed} gagal` : ""}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setImportOpen(false)}>Tutup</Button>
            <Button onClick={doImport} disabled={importing || !file} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="backup-import-submit">
              {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Mulai Impor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

const EMPTY_FORM = { name: "", username: "", password: "", teacher_type: "mapel", wali_kelas: "1", wali_rombel: "", access_start: "", access_end: "", school_id: "" };

function ResetDemoSection({ stats, onDone }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [deleteSubjects, setDeleteSubjects] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo", { confirm, delete_subjects: deleteSubjects });
      const r = data.removed;
      toast.success(`Data demo dihapus: ${r.students} murid, ${r.assignments} tugas, ${r.submissions} pengumpulan, ${r.attendance} absensi, ${r.subjects} mapel.`, { duration: 9000 });
      setOpen(false);
      setConfirm("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus data demo");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-rose-200 shadow-sm" data-testid="reset-demo-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800">Reset Data Demo</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Hapus semua murid, tugas, pengumpulan, absensi, notifikasi, dan akun guru demo sekali klik saat sekolah siap memakai data asli.
              Akun admin dan guru asli tetap aman. Data contoh tidak akan dibuat ulang.
            </p>
            {stats?.demo_reset_at && <p className="text-xs text-emerald-700 mt-2" data-testid="demo-reset-at">Terakhir direset: {stats.demo_reset_at.slice(0, 16).replace("T", " ")}</p>}
          </div>
        </div>
        <Button variant="outline" className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setOpen(true)} data-testid="reset-demo-btn">
          <Trash2 className="w-4 h-4 mr-2" /> Hapus Data Demo
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirm(""); }}>
        <DialogContent className="rounded-2xl" data-testid="reset-demo-dialog">
          <DialogHeader><DialogTitle className="font-display text-rose-700">Hapus Semua Data Demo?</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-rose-50 rounded-xl p-3 text-rose-800">
              Akan dihapus permanen: <b>{stats?.students ?? 0}</b> murid, <b>{stats?.assignments ?? 0}</b> tugas, <b>{stats?.submissions ?? 0}</b> pengumpulan, <b>{stats?.attendance ?? 0}</b> absensi{deleteSubjects ? <>, <b>{stats?.subjects ?? 0}</b> mapel</> : ""}.
            </div>
            <label className="flex items-center gap-3">
              <Switch checked={deleteSubjects} onCheckedChange={setDeleteSubjects} data-testid="reset-demo-subjects-switch" />
              <span className="text-slate-700">Hapus juga mata pelajaran</span>
            </label>
            <div>
              <Label>Ketik <span className="font-mono font-bold">HAPUS</span> untuk konfirmasi</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="HAPUS" className="mt-1 rounded-xl" data-testid="reset-demo-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)} data-testid="reset-demo-cancel">Batal</Button>
            <Button onClick={run} disabled={busy || confirm.trim().toUpperCase() !== "HAPUS"} className="rounded-xl bg-rose-600 hover:bg-rose-700" data-testid="reset-demo-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Hapus Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function TeacherTypeFields({ value, onChange, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label className="text-xs text-slate-500">Jenis Guru</Label>
        <Select value={value.teacher_type} onValueChange={(v) => onChange("teacher_type", v)}>
          <SelectTrigger className="mt-1 rounded-xl" data-testid={`${prefix}-type-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mapel">Guru Mapel (semua kelas)</SelectItem>
            <SelectItem value="wali">Wali Kelas (1 kelas)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {value.teacher_type === "wali" && (
        <>
          <div>
            <Label className="text-xs text-slate-500">Kelas</Label>
            <Select value={String(value.wali_kelas || "1")} onValueChange={(v) => onChange("wali_kelas", v)}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid={`${prefix}-wali-kelas-select`}><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Rombel</Label>
            <Input value={value.wali_rombel || ""} onChange={(e) => onChange("wali_rombel", e.target.value)} placeholder="A" maxLength={3} className="mt-1 rounded-xl uppercase" data-testid={`${prefix}-wali-rombel-input`} />
          </div>
        </>
      )}
    </div>
  );
}

function StudentsSection() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kelas, setKelas] = useState("all");
  const [search, setSearch] = useState("");
  const [badgeDefs, setBadgeDefs] = useState({});
  const [editFor, setEditFor] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", kelas: "1", rombel: "" });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [badgeFor, setBadgeFor] = useState(null);
  const [points, setPoints] = useState(0);
  const [badgeSel, setBadgeSel] = useState([]);
  const [delFor, setDelFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (kelas !== "all") params.kelas = parseInt(kelas);
    if (search.trim()) params.search = search.trim();
    api.get("/admin/students", { params }).then((r) => setStudents(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/admin/badge-defs").then((r) => setBadgeDefs(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [kelas, search]);

  const openEdit = (s) => {
    setEditFor(s);
    setForm({ name: s.name || "", username: s.username || "", kelas: String(s.kelas || "1"), rombel: s.rombel || "" });
  };
  const saveEdit = async () => {
    if (!form.name.trim() || !form.username.trim()) { toast.error("Nama & username wajib diisi"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/students/${editFor.id}`, {
        name: form.name.trim(), username: form.username.trim(),
        kelas: parseInt(form.kelas), rombel: form.rombel.trim().toUpperCase(),
      });
      toast.success("Data murid disimpan");
      setEditFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  const savePw = async () => {
    if (newPw.length < 4) { toast.error("Kata sandi minimal 4 karakter"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/students/${pwFor.id}/password`, { new_password: newPw });
      toast.success(`Sandi ${pwFor.name} diubah menjadi: ${newPw}`, { duration: 8000 });
      setPwFor(null); setNewPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah sandi"); }
    finally { setSaving(false); }
  };
  const resetPw = async (s) => {
    try {
      const { data } = await api.post(`/admin/students/${s.id}/reset-password`);
      toast.success(`Sandi ${s.name} direset: ${data.new_password}`, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mereset sandi"); }
  };
  const toggleActive = async (s, v) => {
    try {
      await api.put(`/admin/students/${s.id}/active`, null, { params: { active: v } });
      toast.success(v ? "Murid diaktifkan" : "Murid dinonaktifkan");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah status"); }
  };
  const openBadges = (s) => { setBadgeFor(s); setPoints(s.points || 0); setBadgeSel(s.badges || []); };
  const saveBadges = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/students/${badgeFor.id}/badges`, { points: parseInt(points) || 0, badges: badgeSel });
      toast.success("Lencana & poin diperbarui");
      setBadgeFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  const removeStudent = async () => {
    try {
      await api.delete(`/admin/students/${delFor.id}`);
      toast.success(`Murid ${delFor.name} dihapus`);
      setDelFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };
  const toggleBadge = (id) => setBadgeSel((b) => (b.includes(id) ? b.filter((x) => x !== id) : [...b, id]));

  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="admin-students-section">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Users className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-lg font-bold text-slate-800">Kelola Akun Murid</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">Edit data, sandi, status aktif, lencana & poin murid, atau hapus akun.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama atau username…" className="rounded-xl pl-9" data-testid="admin-student-search" />
        </div>
        <Select value={kelas} onValueChange={setKelas}>
          <SelectTrigger className="w-36 rounded-xl" data-testid="admin-student-kelas-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kelas</SelectItem>
            {KELAS_OPTS.map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500" data-testid="admin-student-count">{students.length} murid</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
      ) : students.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400 bg-slate-50 rounded-xl p-6 text-center" data-testid="admin-students-empty">Tidak ada murid untuk filter ini.</p>
      ) : (
        <div className="mt-4 space-y-2.5 max-h-[520px] overflow-y-auto pr-1">
          {students.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`admin-student-row-${s.id}`}>
              <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-display font-semibold shrink-0">{s.name?.[0]}</div>
              <div className="flex-1 min-w-[150px]">
                <p className="font-semibold text-slate-800 text-sm">{s.name} <span className="text-xs text-slate-400">· Kelas {s.kelas}{s.rombel || ""}</span></p>
                <p className="text-xs text-slate-500">@{s.username} · <Star className="w-3 h-3 inline text-amber-500" /> {s.points || 0} · {(s.badges || []).length} lencana</p>
              </div>
              {s.active === false && <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Nonaktif</span>}
              <div className="flex items-center gap-1.5">
                <Switch checked={s.active !== false} onCheckedChange={(v) => toggleActive(s, v)} data-testid={`admin-student-active-${s.id}`} />
                <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => openEdit(s)} data-testid={`admin-student-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => openBadges(s)} data-testid={`admin-student-badges-${s.id}`}><Award className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => { setPwFor(s); setNewPw(""); }} data-testid={`admin-student-pw-${s.id}`}><KeyRound className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="rounded-lg h-8 text-slate-500 text-xs" onClick={() => resetPw(s)} data-testid={`admin-student-reset-pw-${s.id}`}>Acak</Button>
                <Button size="sm" variant="outline" className="rounded-lg h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDelFor(s)} data-testid={`admin-student-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editFor} onOpenChange={(v) => !v && setEditFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="admin-student-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Murid</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-student-name-input" /></div>
            <div><Label className="text-xs text-slate-500">Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-student-username-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Kelas</Label>
                <Select value={form.kelas} onValueChange={(v) => setForm({ ...form, kelas: v })}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="admin-student-kelas-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{KELAS_OPTS.map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Rombel</Label><Input value={form.rombel} onChange={(e) => setForm({ ...form, rombel: e.target.value })} placeholder="A" maxLength={3} className="mt-1 rounded-xl uppercase" data-testid="admin-student-rombel-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={saveEdit} disabled={saving} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="admin-student-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(v) => !v && setPwFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="admin-student-password-dialog">
          <DialogHeader><DialogTitle className="font-display">Atur Sandi {pwFor?.name}</DialogTitle></DialogHeader>
          <div>
            <Label>Kata Sandi Baru</Label>
            <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Ketik kata sandi baru" className="mt-1 rounded-xl" data-testid="admin-student-new-password-input" />
          </div>
          <DialogFooter>
            <Button onClick={savePw} disabled={saving} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="admin-student-save-password-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Sandi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!badgeFor} onOpenChange={(v) => !v && setBadgeFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="admin-student-badge-dialog">
          <DialogHeader><DialogTitle className="font-display">Lencana & Poin {badgeFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-500" /> Bintang / Poin</Label>
              <Input type="number" min="0" value={points} onChange={(e) => setPoints(e.target.value)} className="mt-1 rounded-xl" data-testid="admin-student-points-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Lencana</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(badgeDefs).map(([id, def]) => {
                  const Icon = BADGE_ICON_MAP[def.icon] || Award;
                  const on = badgeSel.includes(id);
                  return (
                    <button key={id} type="button" onClick={() => toggleBadge(id)} data-testid={`admin-badge-toggle-${id}`}
                      className={`flex items-center gap-2 rounded-xl p-3 border text-left transition-colors ${on ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-100 opacity-70"}`}>
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center ${on ? "bg-amber-400 text-white" : "bg-slate-200 text-slate-400"}`}><Icon className="w-4 h-4" /></span>
                      <span className="text-xs font-semibold text-slate-700">{def.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setBadgeFor(null)}>Batal</Button>
            <Button onClick={saveBadges} disabled={saving} className="rounded-xl bg-amber-600 hover:bg-amber-700" data-testid="admin-student-save-badges-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus akun murid ${delFor?.name}?`}
        description="Akun murid beserta pengumpulan, absensi, dan notifikasinya akan dihapus permanen." onConfirm={removeStudent} testId="admin-delete-student-dialog" />
    </section>
  );
}

function NotificationsSection() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clearOpen, setClearOpen] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/notifications").then((r) => setList(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const filtered = list.filter((n) => {
    const s = search.trim().toLowerCase();
    return !s || (n.owner_name || "").toLowerCase().includes(s) || (n.message || "").toLowerCase().includes(s);
  });
  const del = async (n) => {
    try { await api.delete(`/admin/notifications/${n.id}`); toast.success("Notifikasi dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus notifikasi"); }
  };
  const clearAll = async () => {
    try {
      const { data } = await api.delete("/admin/notifications");
      toast.success(`${data.deleted} notifikasi dihapus`);
      setClearOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus notifikasi"); }
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="admin-notifications-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><Bell className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800">Kelola Notifikasi</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">Hapus notifikasi tertentu atau bersihkan semua notifikasi guru & murid (berguna untuk menghilangkan notifikasi yang keliru).</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setClearOpen(true)} data-testid="admin-clear-all-notif-btn"><Trash2 className="w-4 h-4 mr-2" /> Hapus Semua</Button>
      </div>

      <div className="mt-4 relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari pemilik atau isi notifikasi…" className="rounded-xl pl-9" data-testid="admin-notif-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
      ) : filtered.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400 bg-slate-50 rounded-xl p-6 text-center" data-testid="admin-notif-empty">Tidak ada notifikasi.</p>
      ) : (
        <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {filtered.map((n) => (
            <div key={n.id} className="flex items-start gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`admin-notif-row-${n.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800">{n.message}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  <span className={`px-1.5 py-0.5 rounded-full ${n.owner_role === "guru" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>{n.owner_name}</span>
                  {" · "}{n.type}{" · "}{(n.created_at || "").slice(0, 16).replace("T", " ")}
                </p>
              </div>
              <Button size="sm" variant="outline" className="rounded-lg h-8 text-rose-600 hover:bg-rose-50" onClick={() => del(n)} data-testid={`admin-notif-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog open={clearOpen} onOpenChange={setClearOpen} title="Hapus semua notifikasi?"
        description="Seluruh notifikasi milik guru dan murid akan dihapus permanen. Tindakan ini tidak bisa dibatalkan." onConfirm={clearAll} testId="admin-clear-notif-dialog" />
    </section>
  );
}

const LOG_ACTION_LABELS = {
  hapus_murid: { label: "Hapus Murid", cls: "bg-rose-50 text-rose-700" },
  ubah_murid: { label: "Ubah Data Murid", cls: "bg-sky-50 text-sky-700" },
  status_murid: { label: "Status Murid", cls: "bg-amber-50 text-amber-700" },
  reset_sandi_murid: { label: "Reset Sandi Murid", cls: "bg-indigo-50 text-indigo-700" },
  ubah_sandi_murid: { label: "Atur Sandi Murid", cls: "bg-indigo-50 text-indigo-700" },
  ubah_lencana: { label: "Lencana/Poin", cls: "bg-emerald-50 text-emerald-700" },
  tambah_guru: { label: "Tambah Guru", cls: "bg-teal-50 text-teal-700" },
  hapus_guru: { label: "Hapus Guru", cls: "bg-rose-50 text-rose-700" },
  reset_sandi_guru: { label: "Reset Sandi Guru", cls: "bg-indigo-50 text-indigo-700" },
  ubah_sandi_guru: { label: "Atur Sandi Guru", cls: "bg-indigo-50 text-indigo-700" },
  hapus_notifikasi: { label: "Hapus Notifikasi", cls: "bg-rose-50 text-rose-700" },
  bersihkan_notifikasi: { label: "Bersihkan Notifikasi", cls: "bg-rose-50 text-rose-700" },
  bersihkan_log: { label: "Bersihkan Log", cls: "bg-slate-100 text-slate-600" },
};

function AdminActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clearOpen, setClearOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/logs").then((r) => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = logs.filter((l) => {
    const s = search.trim().toLowerCase();
    return !s || (l.detail || "").toLowerCase().includes(s) || (l.admin_name || "").toLowerCase().includes(s) || (l.action || "").toLowerCase().includes(s);
  });

  const clearAll = async () => {
    try {
      const { data } = await api.delete("/admin/logs");
      toast.success(`${data.deleted} log dibersihkan`);
      setClearOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membersihkan log"); }
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="admin-logs-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0"><History className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800">Log Aktivitas Admin</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">Catatan setiap tindakan admin (hapus murid/guru, reset sandi, ubah data, kelola notifikasi) lengkap dengan waktu & pelakunya.</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-xl border-slate-300 text-slate-600 hover:bg-slate-50" onClick={() => setClearOpen(true)} disabled={logs.length === 0} data-testid="admin-clear-logs-btn"><Trash2 className="w-4 h-4 mr-2" /> Bersihkan Log</Button>
      </div>

      <div className="mt-4 relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari aksi, admin, atau detail…" className="rounded-xl pl-9" data-testid="admin-logs-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : filtered.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400 bg-slate-50 rounded-xl p-6 text-center" data-testid="admin-logs-empty">{logs.length > 0 ? "Tidak ada hasil untuk pencarian ini." : "Belum ada aktivitas tercatat."}</p>
      ) : (
        <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="admin-logs-list">
          {filtered.map((l) => {
            const a = LOG_ACTION_LABELS[l.action] || { label: l.action, cls: "bg-slate-100 text-slate-600" };
            return (
              <div key={l.id} className="flex items-start gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`admin-log-row-${l.id}`}>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${a.cls}`}>{a.label}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">{l.detail}</p>
                  <p className="text-xs text-slate-400 mt-0.5">oleh <b className="text-slate-500">{l.admin_name || "—"}</b> · {(l.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog open={clearOpen} onOpenChange={setClearOpen} title="Bersihkan semua log aktivitas?"
        description="Seluruh catatan log akan dihapus permanen. Tindakan ini juga akan dicatat sebagai satu log baru." onConfirm={clearAll} testId="admin-clear-logs-dialog" />
    </section>
  );
}

function SchoolsSection({ schools, onReload }) {
  const [open, setOpen] = useState(false);
  const empty = { name: "", operator_name: "", operator_username: "", operator_password: "", access_start: "", access_end: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [delFor, setDelFor] = useState(null);

  useEffect(() => {
    const e = {};
    (schools || []).forEach((s) => { e[s.id] = { active: s.active !== false, access_start: s.access_start || "", access_end: s.access_end || "" }; });
    setEdits(e);
  }, [schools]);

  const setEdit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));

  const create = async () => {
    if (!form.name.trim() || !form.operator_username.trim() || !form.operator_password) { toast.error("Lengkapi nama sekolah & akun operator"); return; }
    if (form.operator_password.length < 4) { toast.error("Sandi operator minimal 4 karakter"); return; }
    setSaving(true);
    try {
      await api.post("/admin/schools", { ...form, access_start: form.access_start || null, access_end: form.access_end || null });
      toast.success("Sekolah & akun operator dibuat");
      setOpen(false); setForm(empty); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat sekolah"); }
    finally { setSaving(false); }
  };

  const saveSchool = async (id) => {
    const e = edits[id] || {};
    await api.put(`/admin/schools/${id}`, { active: e.active, access_start: e.access_start || "", access_end: e.access_end || "" });
    toast.success("Pengaturan sekolah disimpan"); onReload();
  };
  const resetOp = async (id) => { const { data } = await api.post(`/admin/schools/${id}/reset-operator-password`); toast.success(`Sandi operator direset: ${data.new_password}`, { duration: 9000 }); };
  const remove = async () => {
    try { await api.delete(`/admin/schools/${delFor.id}`); toast.success("Sekolah dihapus"); setDelFor(null); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus sekolah"); setDelFor(null); }
  };

  return (
    <section data-testid="schools-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-800 flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-600" /> Sekolah & Operator</h2>
          <p className="text-slate-500 text-sm mt-1">Tiap SD punya 1 akun operator yang mengelola guru & muridnya sendiri. Masa aktif tetap diawasi admin.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="add-school-btn"><Plus className="w-5 h-5 mr-2" /> Tambah Sekolah</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        {(schools || []).map((s) => {
          const e = edits[s.id] || {};
          return (
            <div key={s.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`school-row-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
                  <div>
                    <p className="font-semibold text-slate-800">{s.name}</p>
                    <p className="text-xs text-slate-500">Operator: {s.operator ? `@${s.operator.username}` : "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {e.active ? <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">Aktif</span> : <span className="text-xs text-rose-700 bg-rose-50 px-2 py-1 rounded-full">Nonaktif</span>}
                  <Switch checked={!!e.active} onCheckedChange={(v) => setEdit(s.id, "active", v)} data-testid={`school-active-${s.id}`} />
                </div>
              </div>
              <div className="flex gap-2 mt-3 text-xs">
                <span className="bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full">{s.teacher_count} guru</span>
                <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full">{s.student_count} murid</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Mulai</Label><Input type="date" value={e.access_start || ""} onChange={(ev) => setEdit(s.id, "access_start", ev.target.value)} className="mt-1 rounded-xl" data-testid={`school-start-${s.id}`} /></div>
                <div><Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Berakhir</Label><Input type="date" value={e.access_end || ""} onChange={(ev) => setEdit(s.id, "access_end", ev.target.value)} className="mt-1 rounded-xl" data-testid={`school-end-${s.id}`} /></div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" className="rounded-lg bg-indigo-600 hover:bg-indigo-700" onClick={() => saveSchool(s.id)} data-testid={`save-school-${s.id}`}><Save className="w-4 h-4 mr-1.5" /> Simpan</Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => resetOp(s.id)} disabled={!s.operator} data-testid={`reset-operator-${s.id}`}><KeyRound className="w-4 h-4 mr-1.5" /> Sandi Operator</Button>
                <Button size="sm" variant="outline" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50 ml-auto" onClick={() => setDelFor(s)} data-testid={`delete-school-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus sekolah ${delFor?.name}?`}
        description="Akun operator akan ikut dihapus. Sekolah hanya bisa dihapus jika sudah tidak punya guru & murid." onConfirm={remove} testId="delete-school-dialog" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="add-school-dialog">
          <DialogHeader><DialogTitle className="font-display">Tambah Sekolah</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Sekolah (SD)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" placeholder="Contoh: SD Harapan Bangsa" data-testid="school-name-input" /></div>
            <div><Label>Nama Operator</Label><Input value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} className="mt-1 rounded-xl" placeholder="Nama petugas operator" data-testid="operator-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Username Operator</Label><Input value={form.operator_username} onChange={(e) => setForm({ ...form, operator_username: e.target.value })} className="mt-1 rounded-xl" data-testid="operator-username-input" /></div>
              <div><Label>Kata Sandi</Label><Input value={form.operator_password} onChange={(e) => setForm({ ...form, operator_password: e.target.value })} className="mt-1 rounded-xl" data-testid="operator-password-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Masa Aktif Mulai (opsional)</Label><Input type="date" value={form.access_start} onChange={(e) => setForm({ ...form, access_start: e.target.value })} className="mt-1 rounded-xl" /></div>
              <div><Label>Masa Aktif Berakhir (opsional)</Label><Input type="date" value={form.access_end} onChange={(e) => setForm({ ...form, access_end: e.target.value })} className="mt-1 rounded-xl" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={saving} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="save-school-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Buat Sekolah & Operator</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function MigrationSection({ schools, onReload }) {
  const deployUrl = process.env.REACT_APP_BACKEND_URL || "";
  const [sid, setSid] = useState("");
  const [keyInfo, setKeyInfo] = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [tab, setTab] = useState("pull");
  // pull
  const [srcUrl, setSrcUrl] = useState("");
  const [srcKey, setSrcKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [srcSchools, setSrcSchools] = useState(null);
  const [pickSrc, setPickSrc] = useState("");
  const [pMode, setPMode] = useState("new");
  const [pTarget, setPTarget] = useState("");
  const [pulling, setPulling] = useState(false);
  // file
  const [file, setFile] = useState(null);
  const [fMode, setFMode] = useState("new");
  const [fTarget, setFTarget] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (!sid && schools?.[0]) setSid(schools[0].id); }, [schools, sid]);
  useEffect(() => {
    if (!sid) return;
    setLoadingKey(true); setKeyInfo(null);
    api.get(`/admin/schools/${sid}/migration-key`).then((r) => setKeyInfo(r.data))
      .catch(() => toast.error("Gagal memuat kunci")).finally(() => setLoadingKey(false));
  }, [sid]);

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} disalin`)).catch(() => toast.error("Gagal menyalin"));
  };

  const regen = async () => {
    if (!window.confirm("Ganti kunci migrasi? Kunci lama akan langsung tidak berlaku dan admin lain yang memakai kunci lama harus diberi kunci baru.")) return;
    try {
      const { data } = await api.post(`/admin/schools/${sid}/migration-key/regenerate`);
      setKeyInfo((k) => ({ ...k, migration_key: data.migration_key }));
      toast.success("Kunci baru dibuat. Kunci lama tidak berlaku lagi.");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengganti kunci"); }
  };

  const doExportFile = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/migration/export/${sid}/file`, { responseType: "blob", timeout: 600000 });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "imigrasi.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("File imigrasi diunduh");
    } catch (e) { toast.error("Gagal mengekspor file"); } finally { setExporting(false); }
  };

  const connect = async () => {
    if (!srcUrl.trim() || !srcKey.trim()) { toast.error("Isi URL sumber & kunci"); return; }
    setConnecting(true); setSrcSchools(null); setPickSrc("");
    try {
      const { data } = await api.post("/admin/migration/connect", { source_url: srcUrl.trim(), api_key: srcKey.trim() });
      setSrcSchools(data.schools || []);
      if (data.schools?.[0]) setPickSrc(data.schools[0].id);
      toast.success(`Terhubung — ${data.schools?.length || 0} sekolah ditemukan`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal terhubung ke sumber"); }
    finally { setConnecting(false); }
  };

  const doPull = async () => {
    if (!pickSrc) { toast.error("Pilih sekolah sumber"); return; }
    if (pMode === "merge" && !pTarget) { toast.error("Pilih sekolah tujuan untuk mode gabung"); return; }
    setPulling(true); setResult(null);
    try {
      const { data } = await api.post("/admin/migration/import/pull", {
        source_url: srcUrl.trim(), api_key: srcKey.trim(), source_school_id: pickSrc,
        mode: pMode, target_school_id: pMode === "merge" ? pTarget : null,
      }, { timeout: 900000 });
      setResult(data);
      toast.success("Data berhasil ditarik & diimpor", { duration: 6000 });
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menarik data"); }
    finally { setPulling(false); }
  };

  const doImportFile = async () => {
    if (!file) { toast.error("Pilih file .zip imigrasi"); return; }
    if (fMode === "merge" && !fTarget) { toast.error("Pilih sekolah tujuan untuk mode gabung"); return; }
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("mode", fMode);
      if (fMode === "merge") fd.append("target_school_id", fTarget);
      const { data } = await api.post("/admin/migration/import/file", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 900000,
      });
      setResult(data);
      toast.success("File imigrasi berhasil diimpor", { duration: 6000 });
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengimpor file"); }
    finally { setImporting(false); }
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="migration-section">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"><ArrowRightLeft className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-lg font-bold text-slate-800">Transfer Data Antar Deployment (Imigrasi)</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Pindahkan seluruh data satu sekolah (akun, mapel, tugas, nilai, absensi, kuis, foto) ke deployment DUNIA KELAS lain.
            Bagikan <b>URL + Kunci</b> ke admin tujuan untuk ditarik (PULL), atau unduh/unggah file. Deployment tujuan memakai AI untuk membaca & memeriksa paket.
          </p>
        </div>
      </div>

      {/* EKSPOR / KUNCI */}
      <div className="mt-5 rounded-2xl border border-slate-100 p-4 bg-slate-50/60" data-testid="migration-export-box">
        <p className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2"><Key className="w-4 h-4 text-indigo-600" /> Ekspor / Bagikan Sekolah Ini</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Pilih Sekolah</Label>
            <Select value={sid} onValueChange={setSid}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="migration-school-select"><SelectValue placeholder="Pilih sekolah" /></SelectTrigger>
              <SelectContent>{(schools || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={doExportFile} disabled={exporting || !sid} variant="outline" className="rounded-xl w-full" data-testid="migration-export-file-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Unduh File (.zip)
            </Button>
          </div>
        </div>

        {loadingKey ? (
          <div className="mt-3 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Memuat kunci...</div>
        ) : keyInfo && (
          <div className="mt-3 space-y-2 text-sm">
            <KeyRow label="URL Sumber" value={deployUrl} onCopy={() => copy(deployUrl, "URL")} testid="mig-url" />
            <KeyRow label="ID Sekolah" value={sid} onCopy={() => copy(sid, "ID sekolah")} testid="mig-sid" />
            <KeyRow label="Kunci Sekolah" value={keyInfo.migration_key} onCopy={() => copy(keyInfo.migration_key, "Kunci")} testid="mig-key"
              extra={<Button size="sm" variant="ghost" className="rounded-lg text-slate-500" onClick={regen} data-testid="migration-regen-key-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Ganti</Button>} />
            {keyInfo.global_key && (
              <KeyRow label="Kunci Global (server)" value={keyInfo.global_key} onCopy={() => copy(keyInfo.global_key, "Kunci global")} testid="mig-global"
                icon={<Globe className="w-3.5 h-3.5 text-slate-400" />} />
            )}
            <p className="text-xs text-slate-400">Berikan URL + salah satu kunci kepada admin deployment tujuan untuk menarik data sekolah ini.</p>
          </div>
        )}
      </div>

      {/* IMPOR */}
      <div className="mt-5 rounded-2xl border border-slate-100 p-4" data-testid="migration-import-box">
        <p className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2"><Upload className="w-4 h-4 text-indigo-600" /> Impor ke Deployment Ini</p>
        <div className="flex gap-2 mb-4">
          <Button size="sm" variant={tab === "pull" ? "default" : "outline"} className={`rounded-xl ${tab === "pull" ? "bg-indigo-600 hover:bg-indigo-700" : ""}`} onClick={() => { setTab("pull"); setResult(null); }} data-testid="migration-tab-pull"><Link2 className="w-4 h-4 mr-1.5" /> Tarik via API</Button>
          <Button size="sm" variant={tab === "file" ? "default" : "outline"} className={`rounded-xl ${tab === "file" ? "bg-indigo-600 hover:bg-indigo-700" : ""}`} onClick={() => { setTab("file"); setResult(null); }} data-testid="migration-tab-file"><FileArchive className="w-4 h-4 mr-1.5" /> Unggah File</Button>
        </div>

        {tab === "pull" ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">URL Deployment Sumber</Label><Input value={srcUrl} onChange={(e) => setSrcUrl(e.target.value)} placeholder="https://..." className="mt-1 rounded-xl" data-testid="migration-source-url" /></div>
              <div><Label className="text-xs text-slate-500">Kunci Migrasi</Label><Input value={srcKey} onChange={(e) => setSrcKey(e.target.value)} placeholder="migs_... / global" className="mt-1 rounded-xl" data-testid="migration-source-key" /></div>
            </div>
            <Button onClick={connect} disabled={connecting} variant="outline" className="rounded-xl" data-testid="migration-connect-btn">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} Hubungkan
            </Button>

            {srcSchools && (
              <div className="rounded-xl bg-slate-50 p-3 space-y-3" data-testid="migration-source-schools">
                <div>
                  <Label className="text-xs text-slate-500">Sekolah Sumber</Label>
                  <Select value={pickSrc} onValueChange={setPickSrc}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="migration-pick-source"><SelectValue placeholder="Pilih sekolah sumber" /></SelectTrigger>
                    <SelectContent>{srcSchools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.teacher_count} guru, {s.student_count} murid)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <ModeSelect mode={pMode} setMode={setPMode} target={pTarget} setTarget={setPTarget} schools={schools} prefix="pull" />
                <Button onClick={doPull} disabled={pulling} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="migration-pull-btn">
                  {pulling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />} Mulai Tarik & Impor
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">File imigrasi (.zip)</Label>
              <Input type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 rounded-xl" data-testid="migration-file-input" />
              {file && <p className="text-xs text-slate-500 mt-1">{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</p>}
            </div>
            <ModeSelect mode={fMode} setMode={setFMode} target={fTarget} setTarget={setFTarget} schools={schools} prefix="file" />
            <Button onClick={doImportFile} disabled={importing || !file} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="migration-import-file-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Impor File
            </Button>
          </div>
        )}

        {(pulling || importing) && <p className="text-xs text-slate-400 mt-3 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI sedang membaca & memindahkan data, mohon tunggu...</p>}

        {result && (
          <div className="mt-4 rounded-xl bg-emerald-50 p-4 space-y-2 text-sm" data-testid="migration-result">
            <p className="font-semibold text-emerald-800">Berhasil diimpor (mode: {result.mode}) ke sekolah "{result.school_name}"</p>
            {result.ai_analysis && (
              <div className="rounded-lg bg-white/70 p-3 text-slate-700 border border-emerald-100" data-testid="migration-ai-analysis">
                <p className="font-semibold text-indigo-700 flex items-center gap-1.5 mb-1"><Sparkles className="w-4 h-4" /> Analisis AI</p>
                <p className="whitespace-pre-wrap text-xs leading-relaxed">{result.ai_analysis}</p>
              </div>
            )}
            <div className="text-emerald-800 text-xs">
              {Object.entries(result.collections || {}).map(([k, v]) => <span key={k} className="inline-block mr-3">• {k}: <b>{v}</b></span>)}
              <span className="inline-block mr-3">• foto: <b>{result.photos_restored}</b>{result.photos_failed ? ` (gagal ${result.photos_failed})` : ""}</span>
            </div>
            {result.renamed_users?.length > 0 && (
              <div className="text-amber-800 text-xs bg-amber-50 rounded-lg p-2" data-testid="migration-renames">
                <b>{result.renamed_users.length} username diganti</b> karena bentrok: {result.renamed_users.slice(0, 8).map((r) => `${r.old}→${r.new}`).join(", ")}{result.renamed_users.length > 8 ? "..." : ""}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function KeyRow({ label, value, onCopy, extra, icon, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-36 shrink-0 flex items-center gap-1">{icon} {label}</span>
      <code className="flex-1 min-w-0 truncate text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5" data-testid={testid}>{value || "—"}</code>
      <Button size="sm" variant="ghost" className="rounded-lg text-slate-500" onClick={onCopy} data-testid={`${testid}-copy`}><Copy className="w-3.5 h-3.5" /></Button>
      {extra}
    </div>
  );
}

function ModeSelect({ mode, setMode, target, setTarget, schools, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-slate-500">Mode Impor</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="mt-1 rounded-xl" data-testid={`migration-${prefix}-mode`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">Buat Sekolah Baru (ID & akun baru)</SelectItem>
            <SelectItem value="merge">Gabung ke Sekolah yang Ada</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "merge" && (
        <div>
          <Label className="text-xs text-slate-500">Sekolah Tujuan</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid={`migration-${prefix}-target`}><SelectValue placeholder="Pilih sekolah tujuan" /></SelectTrigger>
            <SelectContent>{(schools || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [pwOpen, setPwOpen] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [delFor, setDelFor] = useState(null);
  const isOperator = user?.role === "operator";
  const [schools, setSchools] = useState([]);
  const [schoolName, setSchoolName] = useState("");

  const removeTeacher = async () => {
    try {
      await api.delete(`/admin/teachers/${delFor.id}`);
      toast.success(`Akun ${delFor.name} dihapus`);
      setDelFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus guru");
    }
  };

  const saveTeacherPw = async () => {
    if (newPw.length < 4) return toast.error("Kata sandi minimal 4 karakter");
    setSaving(true);
    try {
      await api.put(`/admin/teachers/${pwFor.id}/password`, { new_password: newPw });
      toast.success(`Sandi ${pwFor.name} diubah menjadi: ${newPw}`, { duration: 8000 });
      setPwFor(null);
      setNewPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengubah sandi");
    } finally { setSaving(false); }
  };

  const load = (silent = false) => {
    if (silent !== true) setLoading(true);
    const reqs = [api.get("/admin/stats"), api.get("/admin/teachers"),
      user?.role === "operator" ? api.get("/admin/my-school") : api.get("/admin/schools")];
    Promise.all(reqs).then(([s, t, extra]) => {
      setStats(s.data);
      setTeachers(t.data);
      if (user?.role === "operator") setSchoolName(extra?.data?.name || "");
      else setSchools(extra?.data || []);
      const e = {};
      t.data.forEach((x) => {
        e[x.id] = {
          active: x.active !== false, access_start: x.access_start || "", access_end: x.access_end || "",
          teacher_type: x.teacher_type || "mapel", wali_kelas: x.wali_kelas ? String(x.wali_kelas) : "1", wali_rombel: x.wali_rombel || "",
        };
      });
      setEdits(e);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate("/admin"); };

  const create = async () => {
    if (!form.name || !form.username || !form.password) { toast.error("Lengkapi data guru"); return; }
    setSaving(true);
    try {
      await api.post("/admin/teachers", {
        ...form, wali_kelas: form.teacher_type === "wali" ? parseInt(form.wali_kelas) : null,
        wali_rombel: form.teacher_type === "wali" ? form.wali_rombel.trim().toUpperCase() : "",
        access_start: form.access_start || null, access_end: form.access_end || null,
      });
      toast.success("Akun guru dibuat");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat akun");
    } finally { setSaving(false); }
  };

  const saveAccess = async (id) => {
    const e = edits[id];
    await api.put(`/admin/teachers/${id}`, {
      active: e.active, access_start: e.access_start || "", access_end: e.access_end || "",
      teacher_type: e.teacher_type, wali_kelas: e.teacher_type === "wali" ? parseInt(e.wali_kelas) : null,
      wali_rombel: e.teacher_type === "wali" ? (e.wali_rombel || "").trim().toUpperCase() : "",
    });
    toast.success("Pengaturan guru disimpan");
    load();
  };

  const resetPw = async (id, name) => {
    const { data } = await api.post(`/admin/teachers/${id}/reset-password`);
    toast.success(`Sandi ${name} direset: ${data.new_password}`, { duration: 8000 });
  };

  const setEdit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));

  const STAT_CARDS = stats ? [
    { label: "Guru", value: stats.teachers, icon: GraduationCap, color: "bg-teal-50 text-teal-700" },
    { label: "Guru Aktif", value: stats.teachers_active, icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700" },
    { label: "Murid", value: stats.students, icon: Users, color: "bg-amber-50 text-amber-700" },
    { label: "Mapel", value: stats.subjects, icon: BookOpen, color: "bg-sky-50 text-sky-700" },
    { label: "Tugas", value: stats.assignments, icon: ClipboardList, color: "bg-indigo-50 text-indigo-700" },
  ] : [];

  return (
    <div className="grain min-h-screen bg-[#FAF9F5]">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-slate-900/95 text-white">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <p className="font-display font-bold leading-none">{isOperator ? "Panel Operator Sekolah" : "Panel Admin"}</p>
              <p className="text-xs text-slate-300">{isOperator ? (schoolName || "Sekolah") : "DUNIA KELAS"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-300">{user?.name}</span>
            <Button onClick={() => setPwOpen(true)} variant="outline" className="rounded-xl bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="admin-change-password-btn"><KeyRound className="w-4 h-4 mr-1.5" /> Ganti Sandi</Button>
            <Button onClick={doLogout} variant="outline" className="rounded-xl bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-1.5" /> Keluar</Button>
          </div>
        </div>
      </header>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus akun guru ${delFor?.name}?`}
        description={`Akun @${delFor?.username || ""} akan dihapus permanen dan tidak bisa login lagi. Data mapel, tugas, dan nilai murid tetap tersimpan.`}
        onConfirm={removeTeacher} testId="delete-teacher-dialog" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 relative z-[2] space-y-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {STAT_CARDS.map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-5 h-5" /></div>
                  <p className="font-display text-2xl font-bold text-slate-800 mt-2">{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>

            {!isOperator && <SchoolsSection schools={schools} onReload={load} />}

            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-bold text-slate-800">Kelola Akun Guru</h2>
                <p className="text-slate-500 text-sm mt-1">Atur apakah akun guru aktif dan rentang tanggal akses (berlaku dari–sampai).</p>
              </div>
              <Button onClick={() => setOpen(true)} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="add-teacher-btn"><UserPlus className="w-5 h-5 mr-2" /> Tambah Guru</Button>
            </div>

            <div className="space-y-3">
              {teachers.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-dashed border-slate-200" data-testid="teachers-empty">
                  Belum ada guru. Klik "Tambah Guru" untuk membuat akun.
                </div>
              )}
              {teachers.map((t) => {
                const e = edits[t.id] || {};
                return (
                  <div key={t.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`teacher-row-${t.id}`}>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-[180px]">
                        <div className="w-11 h-11 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-display font-semibold">{t.name?.[0]}</div>
                        <div>
                          <p className="font-semibold text-slate-800">{t.name}</p>
                          <p className="text-xs text-slate-500">@{t.username}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${t.teacher_type === "wali" ? "bg-indigo-50 text-indigo-700" : "bg-sky-50 text-sky-700"}`} data-testid={`teacher-type-badge-${t.id}`}>
                        <School className="w-3.5 h-3.5" /> {t.teacher_type === "wali" ? `Wali Kelas ${t.wali_kelas}${t.wali_rombel || ""}` : "Guru Mapel"}
                      </span>
                      <div className="flex items-center gap-2">
                        {e.active ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Aktif</span> : <span className="inline-flex items-center gap-1 text-xs text-rose-700 bg-rose-50 px-2.5 py-1 rounded-full"><XCircle className="w-3.5 h-3.5" /> Nonaktif</span>}
                        <Switch checked={e.active} onCheckedChange={(v) => setEdit(t.id, "active", v)} data-testid={`teacher-active-${t.id}`} />
                      </div>
                    </div>
                    <div className="mt-4">
                      <TeacherTypeFields value={e} onChange={(k, v) => setEdit(t.id, k, v)} prefix={`teacher-${t.id}`} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 mt-4">
                      <div>
                        <Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Akses Mulai</Label>
                        <Input type="date" value={e.access_start || ""} onChange={(ev) => setEdit(t.id, "access_start", ev.target.value)} className="mt-1 rounded-xl" data-testid={`teacher-start-${t.id}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Akses Berakhir</Label>
                        <Input type="date" value={e.access_end || ""} onChange={(ev) => setEdit(t.id, "access_end", ev.target.value)} className="mt-1 rounded-xl" data-testid={`teacher-end-${t.id}`} />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" className="rounded-lg bg-teal-600 hover:bg-teal-700" onClick={() => saveAccess(t.id)} data-testid={`save-access-${t.id}`}><Save className="w-4 h-4 mr-1.5" /> Simpan Akses</Button>
                      <Button size="sm" variant="outline" className="rounded-lg" onClick={() => { setPwFor(t); setNewPw(""); }} data-testid={`set-teacher-pw-${t.id}`}><KeyRound className="w-4 h-4 mr-1.5" /> Atur Sandi</Button>
                      <Button size="sm" variant="ghost" className="rounded-lg text-slate-500" onClick={() => resetPw(t.id, t.name)} data-testid={`reset-teacher-pw-${t.id}`}>Sandi Acak</Button>
                      <Button size="sm" variant="outline" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50 ml-auto" onClick={() => setDelFor(t)} data-testid={`delete-teacher-${t.id}`}><Trash2 className="w-4 h-4 mr-1.5" /> Hapus</Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <StudentsSection />
            {!isOperator && (
              <>
                <NotificationsSection />
                <AdminActivityLog />
                <SourceDownload />
                <AiKeysSection />
                <MigrationSection schools={schools} onReload={() => load(true)} />
                <BackupRestoreSection onDone={load} />
                <ResetDemoSection stats={stats} onDone={load} />
              </>
            )}
          </>
        )}
      </main>

      <Dialog open={!!pwFor} onOpenChange={(v) => !v && setPwFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="set-teacher-password-dialog">
          <DialogHeader><DialogTitle className="font-display">Atur Sandi {pwFor?.name}</DialogTitle></DialogHeader>
          <div>
            <Label>Kata Sandi Baru</Label>
            <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Ketik kata sandi baru" className="mt-1 rounded-xl" data-testid="teacher-new-password-input" />
            <p className="text-xs text-slate-500 mt-2">Sampaikan kata sandi ini kepada guru yang bersangkutan.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveTeacherPw} disabled={saving} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="save-teacher-password-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Sandi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Tambah Akun Guru</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="teacher-name-input" /></div>
            {!isOperator && (
              <div>
                <Label>Sekolah</Label>
                <Select value={form.school_id || ""} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="teacher-school-select"><SelectValue placeholder="Sekolah pertama (default)" /></SelectTrigger>
                  <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-xl" data-testid="teacher-username-input" /></div>
              <div><Label>Kata Sandi</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" data-testid="teacher-password-input" /></div>
            </div>
            <TeacherTypeFields value={form} onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))} prefix="new-teacher" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Akses Mulai (opsional)</Label><Input type="date" value={form.access_start} onChange={(e) => setForm({ ...form, access_start: e.target.value })} className="mt-1 rounded-xl" /></div>
              <div><Label>Akses Berakhir (opsional)</Label><Input type="date" value={form.access_end} onChange={(e) => setForm({ ...form, access_end: e.target.value })} className="mt-1 rounded-xl" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={saving} className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="save-teacher-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Buat Akun</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
